import { useEffect, useId, useMemo } from 'react';

import { useStableRef } from '~/hooks/useStableRef';

import type { TMenuItem, TSubmenuItem } from './CommandMenuProvider.context';

import { useCommandMenuContext } from './CommandMenuProvider.context';

type Props = Omit<TMenuItem, 'id'>;

/**
 * Add an item to the command menu.
 *
 * This component itself doesn't render anything, because the MenuItem needs to be a child of MenuItems.
 * Instead, it adds an item to the items stored in context, and removes it on unmount.
 */
export const CommandMenuItem = ({ group, items, label, onClick, shortcut }: Props) => {
  const id = useId();
  const { addMenuItem, removeMenuItem, updateMenuItem } = useCommandMenuContext();

  const stableOnClick = useStableRef(onClick);
  const stableItems = useStableRef(items);

  // The submenu items are keyed on their labels and call through the ref, so an inline `items`
  // array doesn't re-register the menu item on every render.
  const itemLabels = items?.map((item) => item.label).join('\n');
  const submenuItems = useMemo(
    (): TSubmenuItem[] | undefined =>
      itemLabels?.split('\n').map((itemLabel, index) => ({ label: itemLabel, onClick: () => stableItems.current?.[index]?.onClick() })),
    [itemLabels, stableItems],
  );

  useEffect(
    () => {
      addMenuItem({ id, group, items: submenuItems, label, shortcut, onClick: stableOnClick.current });
      return () => removeMenuItem(id);
    },
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [id],
  );

  useEffect(() => {
    updateMenuItem({ id, group, items: submenuItems, label, shortcut, onClick: stableOnClick.current });
  }, [group, id, label, shortcut, stableOnClick, submenuItems, updateMenuItem]);

  return null;
};
